import { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Typography, Box, Checkbox } from '@mui/material';
import { useLoaderData, useOutletContext, useRevalidator } from 'react-router';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { authedApiFetch } from '../../utils/Api';
import Actions from '../../components/Actions';

// Export User Data through CSV download
import DownloadIcon from '@mui/icons-material/Download';
import { usePapaParse } from 'react-papaparse';

export const loader = async ({ params }) => {
	// Get tenant ID from URI
	const { tenantId } = params;

	return authedApiFetch({
		endpoint: `/tenants/${tenantId}/users`,
		method: 'GET',
	});
};

const UserList = () => {
	const [selected, setSelected] = useState([]);

	// Retrieve loader data
	const users = useLoaderData();

	// Retrieve current user to apply role permissions
	const { tenant, user } = useOutletContext();

	// For imperatively reloading the page data after completing actions
	const revalidator = useRevalidator();

	// Store currently selected users in React state when MUI grid reports selects
	const handleSelectedRows = (selectedRows) => {
		setSelected(selectedRows);
	};

	const roleMap = {
		tenant_read_only: 'Read Only',
		tenant_admin: 'Admin',
		tenant_user: 'User',
	};

	const columns = [
		// { field: 'id', headerName: 'ID', width: 70 },
		{ field: 'first_name', headerName: 'First name', minWidth: 130 },
		{ field: 'last_name', headerName: 'Last name', minWidth: 130 },
		{ field: 'email', headerName: 'Email', minWidth: 250 },
		{ field: 'phone', headerName: 'Phone', minWidth: 150 },
		{
			field: 'role',
			headerName: 'Role',
			minWidth: 100,
			renderCell: (params) => roleMap[params.value],
		},
		{
			field: 'last_login',
			headerName: 'Last Login',
			minWidth: 100,
			renderCell: (params) => {
				if (params.value) {
					const date = new Date(params.value);

					// Format the date to a user-friendly local timezone string
					const dateStr = date.toLocaleDateString('en-US', {
						month: '2-digit',
						day: '2-digit',
						year: '2-digit',
					});
					const timeStr = date.toLocaleTimeString('en-US', {
						hour: '2-digit',
						minute: '2-digit',
						hour12: true,
					});

					return `${dateStr} ${timeStr}`;
				}
			},
		},
		{
			field: 'aime',
			headerName: 'AIMe',
			description: 'Is this user an AIMe User?',
			minWidth: 130,
			align: 'center',
			renderCell: (params) => <Checkbox checked={params.value} disabled />,
		},
		{
			field: 'auth_provider_user_id',
			headerName: 'Altvia Login',
			description:
				'Does this user have login credentials for the Altvia Launchpad?',
			minWidth: 130,
			align: 'center',
			renderCell: (params) => <Checkbox checked={params.value} disabled />,
		},
		{
			field: 'crm',
			headerName: 'CRM',
			description: 'Is this user a CRM User?',
			minWidth: 90,
			align: 'center',
			renderCell: (params) => <Checkbox checked={params.value} disabled />,
		},
		{
			field: 'vdr',
			headerName: 'VDR/Portal',
			description: 'Is this user a VDR/Portal Admin User?',
			minWidth: 130,
			align: 'center',
			renderCell: (params) => <Checkbox checked={params.value} disabled />,
		},
		{
			field: 'analytics',
			headerName: 'Analytics',
			description: 'Is this user an Analytics User?',
			minWidth: 100,
			align: 'center',
			renderCell: (params) => <Checkbox checked={params.value} disabled />,
		},
	];

	const selectedUsers = users.filter((user) => selected.includes(user.id));
	const selectedAndNeedLogin = selectedUsers.filter(
		(user) => !user.auth_provider_user_id
	);

	const handleCreateAltviaLogin = async () => {
		await Promise.all(
			selectedAndNeedLogin.map((user) => {
				return authedApiFetch({
					endpoint: `/users/${user.id}/auth_provider_user`,
					method: 'POST',
				});
			})
		);

		// imperatively revalidate b/c not using React Router action.
		// TODO: refactor so that this goes through a clientAction.
		revalidator.revalidate();
	};

	// Valid re-invite users are users who already have a launchpad login
	// Confirmed users are also invalid, but it's not being filtered out here b/c we don't have this status recorded for checking
	const validReinviteUsers = selectedUsers.filter(
		(user) => user.auth_provider_user_id
	);

	// Re-invite a list of users who already have a launchpad login
	const handleReInviteUsers = async () => {
		const user_ids = validReinviteUsers.map((user) => user.id);

		try {
			await authedApiFetch({
				endpoint: '/auth/reinvite',
				method: 'POST',
				payload: { tenant_id: tenant.id, user_ids: user_ids },
			});

			// imperatively revalidate b/c not using React Router action.
			// TODO: refactor so that this goes through a clientAction.
			revalidator.revalidate();
		} catch (error) {
			// when re-inviting a single confirmed user, backend will raise an error.
			console.error('Failed to re-invite user.', error);
			throw new Error('Failed to re-invite selected user.');
		}
	};

	// Export selected user's data to CSV
	const { jsonToCSV } = usePapaParse();
	const handleExportUserData = async () => {
		try {
			const csv = jsonToCSV(selectedUsers);
			const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;

			const fileName = `${tenant.short_name} User Export - ${new Date()
				.toLocaleString('en-US', {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
				})
				.replace(/\//g, '')}`;
			link.setAttribute('download', fileName);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		} catch (error) {
			throw new Error('Failed to export user data.');
		}
	};

	// Actions to dynamically create buttons and confirmation UI for.
	const actions = [
		{
			name: 'createAltviaLogin',
			label: 'Create Altvia Login',
			icon: <LockOpenIcon />,
			confirmText:
				"This will create users in this tenant's user pool for the" +
				' selected users and send them an email containing their temporary' +
				' password and link to Launchpad. Selected users who already have' +
				' logins will be ignored.',
			disabledCheck: () => selectedAndNeedLogin.length === 0,
			actionHandler: handleCreateAltviaLogin,
		},
		{
			name: 'reInviteUsers',
			label: 'Re-Invite Users',
			icon: <MailOutlineIcon />,
			confirmText:
				'Are you sure you want to re-invite the selected user(s)? This process will send the user a new temporary password to login with. Selected users who are already confirmed will be ignored.',
			successMessage: 'Re-invite successful.',
			disabledCheck: () => validReinviteUsers.length === 0,
			actionHandler: handleReInviteUsers,
		},
		{
			name: 'exportUserData',
			label: 'Export User Data',
			icon: <DownloadIcon />,
			confirmText:
				'Are you sure you want to export csv data for the selected user(s)?',
			successMessage: 'Export successful.',
			disabledCheck: () => selectedUsers.length === 0,
			actionHandler: handleExportUserData,
		},
	];

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				rowGap: '16px',
				height: '100vh',
				paddingTop: '12px',
			}}
		>
			<Typography variant='h4'>Users</Typography>
			{(user.role == 'altvia_admin' || user.role == 'altvia_power_user') && (
				<Actions actions={actions} />
			)}
			<Box sx={{ flexGrow: 1 }}>
				<DataGrid
					checkboxSelection
					autosizeOnMount
					rows={users}
					columns={columns}
					pageSize={10}
					onRowSelectionModelChange={handleSelectedRows}
					initialState={{
						sorting: {
							sortModel: [{ field: 'last_name', sort: 'asc' }],
						},
					}}
				/>
			</Box>
		</Box>
	);
};

export default UserList;
